import React, { useState } from 'react';
import {
  Modal,
  Typography,
  Form,
  Input,
  Button,
  Checkbox,
  PhoneNumber,
  DatePicker,
  Textarea,
} from '@crisellevaldez/speculo-ui';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { contactSchema, ContactFormData } from '../../../schemas/contact.schema';

interface ContactFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: ContactFormData) => Promise<void>;
}

export const ContactFormModal: React.FC<ContactFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
    watch,
    setError,
    trigger,
  } = useForm<ContactFormData>({
    mode: 'onChange',
    resolver: zodResolver(contactSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      phoneStatus: '',
      email: '',
      address: {
        street: '',
        city: '',
        state: '',
        zip: '',
      },
      opt_in: false,
      opt_in_date: '',
      opt_in_desc: '',
      call_tester: false,
    },
  });

  const handleFormSubmit = async (data: ContactFormData) => {
    try {
      setIsSubmitting(true);
      await onSubmit(data);
      reset();
      onClose();
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'Email already exists') {
          setError('email', {
            type: 'manual',
            message: error.message,
          });
        } else {
          // Default to phoneNumber for other errors (including 'Phone number already exists')
          setError('phoneNumber', {
            type: 'manual',
            message: error.message,
          });
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Convert string to Date for DatePicker
  const optInDateValue = watch('opt_in_date');
  const optInDate = optInDateValue ? new Date(optInDateValue) : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header>
        <Typography variant="h3">Add Contact</Typography>
      </Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Form.Control required>
                <Form.Label>First name</Form.Label>
                <Input
                  placeholder="Enter value"
                  {...register('firstName')}
                  error={errors.firstName?.message}
                />
              </Form.Control>
              <Form.Control>
                <Form.Label>Last name</Form.Label>
                <Input
                  placeholder="Enter value"
                  {...register('lastName')}
                  error={errors.lastName?.message}
                />
              </Form.Control>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Form.Control>
                <Form.Label>Phone number</Form.Label>
                <PhoneNumber
                  value={watch('phoneNumber')}
                  onChange={(value) => {
                    setValue('phoneNumber', value || '', { shouldValidate: true });
                    trigger('phoneNumber');
                  }}
                  error={errors.phoneNumber?.message}
                  defaultCountry="US"
                  placeholder="Enter phone number"
                  isLoading={isSubmitting}
                />
              </Form.Control>
              <Form.Control>
                <Form.Label>Email</Form.Label>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  {...register('email')}
                  error={errors.email?.message}
                />
              </Form.Control>
            </div>

            <div className="space-y-4">
              <Typography variant="h6">Address</Typography>

              <Form.Control>
                <Form.Label>Street</Form.Label>
                <Input
                  placeholder="Enter value"
                  {...register('address.street')}
                  error={errors.address?.street?.message}
                />
              </Form.Control>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Form.Control>
                  <Form.Label>City</Form.Label>
                  <Input
                    placeholder="Enter value"
                    {...register('address.city')}
                    error={errors.address?.city?.message}
                  />
                </Form.Control>
                <Form.Control>
                  <Form.Label>State</Form.Label>
                  <Input
                    placeholder="Enter value"
                    {...register('address.state')}
                    error={errors.address?.state?.message}
                  />
                </Form.Control>
                <Form.Control>
                  <Form.Label>Zip</Form.Label>
                  <Input
                    placeholder="Enter value"
                    {...register('address.zip')}
                    error={errors.address?.zip?.message}
                  />
                </Form.Control>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="optedIn"
                  checked={watch('opt_in')}
                  onChange={(e) => setValue('opt_in', (e.target as HTMLInputElement).checked)}
                />
                <Form.Label htmlFor="optedIn" className="mb-0">
                  Has this contact Opted In?
                </Form.Label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Form.Control>
                  <Form.Label>Opt-in Date</Form.Label>
                  <DatePicker
                    value={optInDate && !isNaN(optInDate.getTime()) ? optInDate : null}
                    onChange={(date) => {
                      setValue('opt_in_date', date ? date.toISOString().split('T')[0] : '', {
                        shouldValidate: true,
                      });
                    }}
                    placeholder="Select date"
                    error={errors.opt_in_date?.message}
                  />
                </Form.Control>

                <Form.Control>
                  <Form.Label>Opt-in Description</Form.Label>
                  <Textarea
                    placeholder="How did they opt in?"
                    {...register('opt_in_desc')}
                    rows={3}
                  />
                </Form.Control>
              </div>
            </div>

          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex w-full items-center justify-between gap-3">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="testContact"
              checked={watch('call_tester')}
              onChange={(e) => setValue('call_tester', (e.target as HTMLInputElement).checked)}
            />
            <Form.Label className="mb-0" htmlFor="testContact">
              Use Contact for Testing Flows
            </Form.Label>
          </div>
          <Button
            onClick={handleSubmit(handleFormSubmit)}
            disabled={isSubmitting || !isValid}
            isLoading={isSubmitting}
          >
            Create Contact
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
